import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Loader2, Users, ArrowLeft } from 'lucide-react';
import { toast } from '../components/ui/sonner';
import { invitationApi } from '../services/api';
import { GoogleAuthButton } from '../components/GoogleAuthButton';

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, demoLogin, verifyTwoFactor } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [pendingInvitation, setPendingInvitation] = useState(null);
  // 2FA challenge state
  const [twoFactor, setTwoFactor] = useState(null); // { pendingToken }
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    const inviteToken = sessionStorage.getItem('pendingInvitation');
    if (inviteToken) {
      setPendingInvitation(inviteToken);
    }
  }, []);

  const finishLogin = async () => {
    if (pendingInvitation) {
      try {
        const response = await invitationApi.accept(pendingInvitation);
        sessionStorage.removeItem('pendingInvitation');
        toast.success('Invitation accepted! You now have access to the estate.');
        navigate(`/estates/${response.data.estate_id}`);
        return;
      } catch (inviteError) {
        console.error('Failed to accept invitation:', inviteError);
        const message = inviteError.response?.data?.detail || '';
        if (message.includes('different email')) {
          toast.error('This invitation was sent to a different email address.');
        }
        sessionStorage.removeItem('pendingInvitation');
      }
    }
    navigate('/dashboard');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.requires2fa) {
        setTwoFactor({ pendingToken: result.pendingToken });
        setLoading(false);
        return;
      }
      await finishLogin();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to login. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2fa = async (e) => {
    e.preventDefault();
    setError('');
    setVerifying(true);
    try {
      await verifyTwoFactor(twoFactor.pendingToken, twoFactorCode.trim());
      await finishLogin();
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid authentication code. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleDemoLogin = useCallback(async () => {
    setError('');
    setDemoLoading(true);
    try {
      const { estate_id } = await demoLogin();
      toast.success('Welcome! You are now viewing the demo estate.');
      if (estate_id) navigate(`/estates/${estate_id}`);
      else navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not start the demo. Please try again.');
    } finally {
      setDemoLoading(false);
    }
  }, [demoLogin, navigate]);

  // Auto-trigger demo login when the page is opened with `?demo=1`, which
  // is how the "Live Demo" nav + footer links land here. Guarded so it
  // fires only once even under React 18 StrictMode double-effect.
  useEffect(() => {
    if (searchParams.get('demo') === '1' && !demoLoading) {
      handleDemoLogin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-ep-background flex" data-testid="login-page">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-ep-primary p-12 flex-col justify-between">
        <div>
          <Link to="/" className="inline-flex items-center gap-1 mb-12">
            <span className="font-heading text-2xl font-bold text-white">Estate</span>
            <span className="font-heading text-2xl font-bold text-ep-secondary">Pro</span>
            <span className="font-heading text-2xl font-bold text-white">Filer</span>
          </Link>
          
          <h1 className="font-heading text-4xl font-bold text-white leading-tight mb-6">
            Welcome back to your<br />
            <span className="italic text-ep-secondary">estate dashboard</span>
          </h1>
          <p className="text-white/70 text-lg">
            Continue managing your estates with clarity and confidence.
          </p>
        </div>
        
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
              <span className="text-xl">🔒</span>
            </div>
            <div>
              <p className="text-white font-medium">Bank-Level Security</p>
              <p className="text-white/60 text-sm">Your data is encrypted and protected</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
              <span className="text-xl">📊</span>
            </div>
            <div>
              <p className="text-white font-medium">Real-Time Tracking</p>
              <p className="text-white/60 text-sm">Monitor your estate progress instantly</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <Link to="/" className="inline-flex items-center gap-1">
              <span className="font-heading text-2xl font-bold text-ep-primary">Estate</span>
              <span className="font-heading text-2xl font-bold text-ep-secondary">Pro</span>
              <span className="font-heading text-2xl font-bold text-ep-primary">Filer</span>
            </Link>
          </div>

          {/* Back to Home */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-ep-text-muted hover:text-ep-text mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to home</span>
          </Link>

          <div className="mb-8">
            <h2 className="font-heading text-3xl font-bold text-ep-text mb-2">
              {twoFactor ? 'Two-factor authentication' : 'Sign in'}
            </h2>
            <p className="text-ep-text-secondary">
              {twoFactor
                ? 'Enter the 6-digit code from your authenticator app, or a backup recovery code.'
                : 'Enter your credentials to access your account'}
            </p>
          </div>

          {/* 2FA challenge step */}
          {twoFactor && (
            <form onSubmit={handleVerify2fa} className="space-y-6" data-testid="twofa-login-form">
              {error && (
                <div className="bg-red-50 text-red-700 p-4 rounded-xl text-sm border border-red-200" data-testid="twofa-error">
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="twofa-code" className="text-ep-text">Authentication code</Label>
                <Input
                  id="twofa-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  placeholder="123456"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value)}
                  required
                  className="h-12 bg-white border-ep-border focus:border-ep-primary focus:ring-ep-primary/20 tracking-widest text-center text-lg"
                  data-testid="twofa-code-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-12 bg-ep-primary hover:bg-ep-primary-dark text-white font-medium"
                disabled={verifying}
                data-testid="twofa-verify-btn"
              >
                {verifying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying…</> : 'Verify & sign in'}
              </Button>
              <button
                type="button"
                onClick={() => { setTwoFactor(null); setTwoFactorCode(''); setError(''); }}
                className="w-full text-center text-sm text-ep-text-muted hover:text-ep-text transition-colors"
                data-testid="twofa-back-btn"
              >
                Use a different account
              </button>
            </form>
          )}

          {!twoFactor && (
          <>
          {pendingInvitation && (
            <div className="mb-6 p-4 bg-ep-primary/5 border border-ep-primary/20 rounded-xl">
              <div className="flex items-center gap-2 text-ep-primary mb-1">
                <Users className="w-4 h-4" />
                <span className="text-sm font-medium">Pending Invitation</span>
              </div>
              <p className="text-sm text-ep-text-secondary">
                Sign in to accept your estate collaboration invitation.
              </p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 text-red-700 p-4 rounded-xl text-sm border border-red-200">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-ep-text">Email address</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 bg-white border-ep-border focus:border-ep-primary focus:ring-ep-primary/20"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-ep-text">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-ep-primary hover:underline font-medium"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 bg-white border-ep-border focus:border-ep-primary focus:ring-ep-primary/20 pr-12"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-ep-text-muted hover:text-ep-text transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 bg-ep-primary hover:bg-ep-primary-dark text-white font-medium"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in...</>
              ) : pendingInvitation ? (
                'Sign In & Accept Invitation'
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {/* Judge / prospect one-click demo */}
          <div className="mt-6 relative">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t border-ep-border/60"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-ep-background px-2 text-ep-text-muted tracking-wider">or</span>
            </div>
          </div>

          <div className="mt-6">
            <GoogleAuthButton disabled={loading || demoLoading} testId="google-login-btn" />
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={handleDemoLogin}
            disabled={demoLoading || loading}
            className="w-full h-12 mt-3 border-ep-primary/30 text-ep-primary hover:bg-ep-primary/5 font-medium"
            data-testid="demo-login-btn"
          >
            {demoLoading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading the demo…</>
            ) : (
              <>Try the live demo — no signup required</>
            )}
          </Button>
          <p className="mt-2 text-center text-xs text-ep-text-muted">
            Explores a pre-populated estate with real data, tasks, cashflow, and heirs.
          </p>

          <p className="mt-8 text-center text-ep-text-secondary">
            Don't have an account?{' '}
            <Link to="/register" className="text-ep-primary hover:underline font-medium">
              Create one
            </Link>
          </p>
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
